import {type ComponentType, useId, useState} from 'react'
import {useDetectedOS} from '@github-ui/swp-core/lib/hooks/use-detected-os'
import {getPrimerIcon} from '@github-ui/swp-core/lib/utils/icons'
import type {Icon} from '@primer/octicons-react'
import {RiverBreakoutTabs, Link, Text} from '@primer/react-brand/esm'
import {CommandActionMenu} from '../../_components/CommandActionMenu/CommandActionMenu'
import {LogoActionMenu} from '../../_components/LogoActionMenu/LogoActionMenu'
import {HeroCarouselBackground} from './HeroCarouselBackground'
import {HeroSlideMedia} from './HeroSlideMedia'
import {pickSlideMedia} from './hero-slide-media'

import {
  findByIdWithSchema,
  findAllByContentType,
  findByContentType,
  GenericContentSchema,
  GenericGroupSchema,
  GenericAssetSchema,
  type GenericContent,
  type GenericGroup,
} from '../../../../../brand/lib/types/contentful'
import {useGenericCtaAction} from '../../../../../brand/components/GenericCtaAction/use-generic-cta-action'

import styles from './HeroSection.module.css'

import CliDemo from './_demos/cli/Index'

// Maps the `demo` entry's htmlId in Contentful to its component.
const DEMOS: Partial<Record<string, ComponentType<{title?: string}>>> = {
  cli: CliDemo,
}

// A slide's `demo` entry, when its htmlId maps to a known demo component.
function getSlideDemo(content: GenericContent['fields']['content']) {
  const demo = findByIdWithSchema(content, 'demo', GenericContentSchema)
  const Demo = demo?.fields.htmlId ? DEMOS[demo.fields.htmlId] : undefined
  return Demo ? {Demo, title: demo?.fields.heading} : undefined
}

type Props = {
  component: GenericGroup
  heading?: string
}

function getCtaVariant(carouselId: string | undefined, selectedSlideId: string | undefined) {
  return carouselId !== undefined && carouselId === selectedSlideId ? 'primary' : 'secondary'
}

export function HeroCarousel({component, heading}: Props) {
  const items = component.fields.content
  const os = useDetectedOS()
  const [selectedIndex, setSelectedIndex] = useState(0)

  // Slides
  // RiverBreakoutTabs skips items without a heading or visual, so filter them out here
  // to keep `selectedIndex` aligned with the slides that are actually rendered.
  const slides = findAllByContentType(items, 'genericContent').filter(
    item =>
      item.fields.heading &&
      (getSlideDemo(item.fields.content) || item.fields.media?.some(m => m.fields.asset.fields.file)),
  )
  const selectedSlideId = slides[selectedIndex]?.sys.id

  // App
  const appCarousel = findByIdWithSchema(items, 'appCarousel', GenericContentSchema)
  const appCarouselContent = appCarousel?.fields.content
  const appCtaContent = findByContentType(appCarouselContent, 'downloadOptions')
  const appCta = useGenericCtaAction(appCtaContent, {
    analytics: {tag: 'button', context: 'CTAs', location: 'hero'},
    variant: getCtaVariant(appCarousel?.sys.id, selectedSlideId),
  })

  // CLI
  const cliCarousel = findByIdWithSchema(items, 'cliCarousel', GenericContentSchema)
  const cliCarouselContent = cliCarousel?.fields.content
  const cliCta = findByIdWithSchema(cliCarouselContent, 'cliCta', GenericGroupSchema)

  // IDE
  const ideCarousel = findByIdWithSchema(items, 'ideCarousel', GenericContentSchema)
  const ideCarouselContent = ideCarousel?.fields.content
  const ideCta = findByIdWithSchema(ideCarouselContent, 'ideCta', GenericGroupSchema)

  // Background
  const background = findByIdWithSchema(items, 'carouselBackground', GenericAssetSchema)

  // Temp test
  const tempTest = findByIdWithSchema(items, 'tempTest', GenericContentSchema)

  // eslint-disable-next-line @eslint-react/naming-convention-id-name
  const descriptionIdPrefix = useId()
  const accessibleHeading = heading ?? slides.at(0)?.fields.heading
  if (!accessibleHeading || slides.length === 0) return null

  return (
    <>
      <RiverBreakoutTabs
        backgroundVisual={background ? <HeroCarouselBackground component={background} /> : null}
        imagePosition="block-end"
        backgroundVisualFullBleed
        selectedIndex={selectedIndex}
        onChange={setSelectedIndex}
        className={styles.RiverBreakoutTabs}
      >
        <RiverBreakoutTabs.A11yHeading as="h2">{accessibleHeading}</RiverBreakoutTabs.A11yHeading>

        {slides.map(item => {
          const {label, heading: itemHeading, media, content} = item.fields
          const icon = getPrimerIcon(label?.fields.icon) as Icon
          // const link = getGenericContentLink(item)
          const slideMedia = pickSlideMedia(media, os)

          const demo = getSlideDemo(content)

          return (
            <RiverBreakoutTabs.Item key={item.sys.id} className={styles.RiverBreakoutTabsItem}>
              {icon ? <RiverBreakoutTabs.Icon icon={icon} /> : null}
              {itemHeading ? <RiverBreakoutTabs.Heading>{itemHeading}</RiverBreakoutTabs.Heading> : null}

              <RiverBreakoutTabs.Content>
                {/*{link ? (
                  <Link
                    href={link.fields.href}
                    {...getAnalyticsEvent({
                      action: link.fields.text,
                      tag: 'button',
                      context: 'CTAs',
                      location: 'hero carousel',
                    })}
                  >
                    {link.fields.text}
                  </Link>
                ) : null}*/}
              </RiverBreakoutTabs.Content>

              {demo ? (
                <RiverBreakoutTabs.Visual className={styles.RiverBreakoutTabsVisual}>
                  <demo.Demo title={demo.title} />
                </RiverBreakoutTabs.Visual>
              ) : slideMedia ? (
                <RiverBreakoutTabs.Visual className={styles.RiverBreakoutTabsVisual}>
                  <HeroSlideMedia
                    media={slideMedia}
                    title={itemHeading}
                    descriptionId={`${descriptionIdPrefix}-${item.sys.id}`}
                  />
                </RiverBreakoutTabs.Visual>
              ) : null}
            </RiverBreakoutTabs.Item>
          )
        })}
      </RiverBreakoutTabs>

      {/* CTAs */}
      {/* Will be moved into RiverBreakoutTabs once support is added */}
      <div className={styles.actionsWrapper}>
        <div className={styles.actions}>
          {/* App */}
          <div>
            {appCta ? (
              <>
                {appCta.action}
                {appCta.feedback}
              </>
            ) : null}
          </div>

          {/* CLI */}
          <div>
            {cliCta ? (
              <CommandActionMenu
                component={cliCta}
                location="hero"
                variant={getCtaVariant(cliCarousel?.sys.id, selectedSlideId)}
              />
            ) : null}
          </div>

          {/* IDE */}
          <div>
            {ideCta ? (
              <LogoActionMenu
                component={ideCta}
                location="hero"
                variant={getCtaVariant(ideCarousel?.sys.id, selectedSlideId)}
              />
            ) : null}
          </div>
        </div>
      </div>

      {/* Temp test */}
      {/* https://github.com/github/brand-experience/issues/650 */}
      {tempTest ? (
        <RiverBreakoutTabs>
          <RiverBreakoutTabs.A11yHeading>Plan, review, and ship with Copilot</RiverBreakoutTabs.A11yHeading>

          <RiverBreakoutTabs.Item>
            <RiverBreakoutTabs.Heading>Plan from your backlog</RiverBreakoutTabs.Heading>
            <RiverBreakoutTabs.Content>
              <Text>Break large issues into practical implementation steps.</Text>
              <Link href="#">Learn more</Link>
            </RiverBreakoutTabs.Content>
            <RiverBreakoutTabs.Visual>
              <Text>Visual 1</Text>
            </RiverBreakoutTabs.Visual>
          </RiverBreakoutTabs.Item>

          <RiverBreakoutTabs.Item>
            <RiverBreakoutTabs.Heading>Review with context</RiverBreakoutTabs.Heading>
            <RiverBreakoutTabs.Content>
              <Text>Keep discussion, suggestions, and diffs together while you review.</Text>
              <Link href="#">Explore reviews</Link>
            </RiverBreakoutTabs.Content>
            <RiverBreakoutTabs.Visual>
              <Text>Visual 2</Text>
            </RiverBreakoutTabs.Visual>
          </RiverBreakoutTabs.Item>

          <RiverBreakoutTabs.Item>
            <RiverBreakoutTabs.Heading>Merge with confidence</RiverBreakoutTabs.Heading>
            <RiverBreakoutTabs.Content>
              <Text>Use checks and approvals to move from code to merge safely.</Text>
              <Link href="#">See merge controls</Link>
            </RiverBreakoutTabs.Content>
            <RiverBreakoutTabs.Visual>
              <Text>Visual 3</Text>
            </RiverBreakoutTabs.Visual>
          </RiverBreakoutTabs.Item>
        </RiverBreakoutTabs>
      ) : null}
    </>
  )
}
